/**
 * src/components/shared/TaskDetailsForm.jsx — Detail fields for TaskDetailPanel
 * AC Nautitech Executive Assistant
 *
 * Budget hours, start/due dates, assignee/project, department/bundle.
 * Extracted from TaskDetailPanel.jsx to reduce file size.
 */

import { useState, useMemo } from 'react';
import { Calendar, Clock, Edit3, Save } from 'lucide-react';
import { Avatar } from '../SharedUI';

const inp = {
  width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--bdr)',
  background: 'var(--card)', fontSize: 13, color: 'var(--t1)', outline: 'none', minHeight: 44,
};

const editBtn = {
  background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', padding: '2px 6px',
};

const saveBtn = {
  padding: '8px 14px', borderRadius: 8, background: 'var(--navy-surface)', color: 'white',
  fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
};

const glassBox = {
  background: 'var(--glass)', borderRadius: 10, padding: 12, border: '1px solid var(--bdr)',
};

const labelRow = {
  fontSize: 11, color: 'var(--tm)', marginBottom: 4,
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
};

// Map the server-update keys this form sends onto the display columns it reads,
// so the local optimistic overlay echoes a save instantly (e.g. {start} ->
// start_date). Keeps the panel snappy before the change round-trips back as a
// new prop (slow on mobile, absent offline).
function toDisplayShape(updates) {
  const o = {};
  if ('start' in updates) o.start_date = updates.start;
  if ('due' in updates) o.due_date = updates.due;
  if ('estimated_hours' in updates) o.estimated_hours = updates.estimated_hours;
  if ('assignee_id' in updates) { o.assignee_id = updates.assignee_id; o.user_profiles = null; }
  if ('department_id' in updates) o.department_id = updates.department_id;
  if ('bundle_id' in updates) o.bundle_id = updates.bundle_id;
  if ('project_id' in updates) o.project_id = updates.project_id;
  return o;
}

export default function TaskDetailsForm({
  task,
  people,
  projects,
  bundles,
  peopleLookup,
  projectLookup,
  departments,
  rollups,
  saving,
  onUpdate,
}) {
  // Local optimistic overlay on top of the prop. Every field repaints the
  // instant it's saved instead of waiting for the global tasks re-render
  // round-trip. Resets per task via the key={details-<id>} remount in
  // TaskDetailPanel.
  const [optimistic, setOptimistic] = useState({});
  const f = { ...(task || {}), ...optimistic };

  const assigneeId = f.assignee_id || '';
  const assignee = f.user_profiles?.display_name
    || (assigneeId ? (peopleLookup[assigneeId] || 'Unknown') : 'Unassigned');

  const projectId = f.project_id || '';
  const projName = f.projects?.project_name
    || (projectId ? (projectLookup[projectId] || projects.find(p => p.id === projectId)?.project_name || '') : '');

  const isOverdue = f.due_date && new Date(f.due_date) < new Date() && f.status !== 'done';
  const taskHours = rollups?.taskRollups?.get?.(task.id) || rollups?.byTask?.[task.id];

  const projectBundles = useMemo(() => {
    if (!f.project_id) return [];
    return (bundles || []).filter(b => b.project_id === f.project_id);
  }, [bundles, f.project_id]);

  // Editing states
  const [editingAssignee, setEditingAssignee] = useState(false);
  const [editingDue, setEditingDue] = useState(false);
  const [editingStart, setEditingStart] = useState(false);
  const [editingBudget, setEditingBudget] = useState(false);

  // Draft values
  const [startDraft, setStartDraft] = useState(f.start_date || '');
  const [dueDraft, setDueDraft] = useState(f.due_date || '');
  const [budgetDraft, setBudgetDraft] = useState(f.estimated_hours || '');

  const doUpdate = async (updates) => {
    const applied = toDisplayShape(updates);
    setOptimistic(prev => ({ ...prev, ...applied }));
    try {
      const result = await onUpdate(updates);
      if (result && result.success === false) {
        setOptimistic(prev => {
          const next = { ...prev };
          for (const k of Object.keys(applied)) delete next[k];
          return next;
        });
      }
    } catch (e) {
      console.warn('[TaskDetailsForm] field update failed:', e?.message);
      setOptimistic(prev => {
        const next = { ...prev };
        for (const k of Object.keys(applied)) delete next[k];
        return next;
      });
    }
  };

  // Reset handler — called by parent via key prop change on task.id
  // (React will remount the component when key changes, resetting all state)

  return (
    <>
      {/* Budget & Actual Hours */}
      <div style={glassBox}>
        <div style={{ ...labelRow, marginBottom: 8 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Clock size={12} /> Budget Hours
          </span>
          <button onClick={() => setEditingBudget(v => !v)} style={editBtn}>
            <Edit3 size={12} />
          </button>
        </div>
        {editingBudget ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="number" step="0.5" min="0" value={budgetDraft}
              onChange={e => setBudgetDraft(e.target.value)}
              placeholder="e.g. 8" style={{ ...inp, flex: 1 }} autoFocus />
            <button onClick={() => { doUpdate({ estimated_hours: budgetDraft ? parseFloat(budgetDraft) : null }); setEditingBudget(false); }}
              disabled={saving} style={saveBtn}>
              <Save size={14} />
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', cursor: 'pointer' }}
            onClick={() => setEditingBudget(true)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (() => setEditingBudget(true))(e); } }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--accent)' }}>
                {f.estimated_hours ? `${f.estimated_hours}h` : '\u2014'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--tm)' }}>Budget</div>
            </div>
            {taskHours && taskHours.actual > 0 && (
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: taskHours.health === 'over-hours' ? 'var(--bad)' : 'var(--ok)' }}>
                  {taskHours.actual}h
                </div>
                <div style={{ fontSize: 11, color: 'var(--tm)' }}>Actual</div>
              </div>
            )}
            {f.estimated_hours > 0 && taskHours?.actual > 0 && (
              <div style={{ flex: 1, textAlign: 'right' }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: (taskHours.actual / f.estimated_hours) > 1 ? 'var(--bad)' : 'var(--ok)' }}>
                  {Math.round((taskHours.actual / f.estimated_hours) * 100)}%
                </div>
                <div style={{ fontSize: 11, color: 'var(--tm)' }}>Used</div>
              </div>
            )}
          </div>
        )}
        {f.estimated_hours > 0 && taskHours?.actual > 0 && !editingBudget && (
          <div style={{ marginTop: 8, height: 4, borderRadius: 2, background: 'var(--bdr)', overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 2, transition: 'width 300ms ease',
              width: `${Math.min((taskHours.actual / f.estimated_hours) * 100, 100)}%`,
              background: (taskHours.actual / f.estimated_hours) > 1 ? 'var(--bad)'
                : (taskHours.actual / f.estimated_hours) > 0.8 ? 'var(--warn)' : 'var(--ok)',
            }} />
          </div>
        )}
      </div>

      {/* Start & Due dates */}
      <div style={{ display: 'flex', gap: 8 }}>
        {/* Start date */}
        <div style={{ flex: 1, ...glassBox }}>
          <div style={labelRow}>
            Start
            <button onClick={() => setEditingStart(v => !v)} style={editBtn}>
              <Edit3 size={12} />
            </button>
          </div>
          {editingStart ? (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="date" value={startDraft} onChange={e => setStartDraft(e.target.value)}
                style={{ ...inp, flex: 1 }} />
              <button onClick={() => setStartDraft(new Date().toISOString().split('T')[0])} type="button" style={{ padding: '6px 10px', borderRadius: 8, background: 'var(--glass)', border: '1px solid var(--bdr)', color: 'var(--accent)', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>Today</button>
              <button onClick={() => { doUpdate({ start: startDraft || null }); setEditingStart(false); }}
                disabled={saving} style={saveBtn}>
                <Save size={14} />
              </button>
            </div>
          ) : (
            <div style={{
              fontSize: 14, fontWeight: 600, color: 'var(--t1)',
              display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer',
            }} onClick={() => setEditingStart(true)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (() => setEditingStart(true))(e); } }}>
              <Calendar size={14} />
              {f.start_date ? new Date(f.start_date).toLocaleDateString('en', { month: 'short', day: 'numeric' }) : 'Set start'}
            </div>
          )}
        </div>

        {/* Due date */}
        <div style={{
          flex: 1, ...glassBox,
          background: isOverdue ? 'rgba(229,62,62,0.08)' : 'var(--glass)',
        }}>
          <div style={labelRow}>
            Due
            <button onClick={() => setEditingDue(v => !v)} style={editBtn}>
              <Edit3 size={12} />
            </button>
          </div>
          {editingDue ? (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="date" value={dueDraft} onChange={e => setDueDraft(e.target.value)}
                style={{ ...inp, flex: 1 }} />
              <button onClick={() => setDueDraft(new Date().toISOString().split('T')[0])} type="button" style={{ padding: '6px 10px', borderRadius: 8, background: 'var(--glass)', border: '1px solid var(--bdr)', color: 'var(--accent)', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>Today</button>
              <button onClick={() => { doUpdate({ due: dueDraft || null }); setEditingDue(false); }}
                disabled={saving} style={saveBtn}>
                <Save size={14} />
              </button>
            </div>
          ) : (
            <div style={{
              fontSize: 14, fontWeight: 600, color: isOverdue ? 'var(--bad)' : 'var(--t1)',
              display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer',
            }} onClick={() => setEditingDue(true)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (() => setEditingDue(true))(e); } }}>
              <Calendar size={14} />
              {f.due_date ? new Date(f.due_date).toLocaleDateString('en', { month: 'short', day: 'numeric' }) : 'Set due'}
            </div>
          )}
        </div>
      </div>

      {/* Assignee + Project row */}
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1, ...glassBox, padding: '10px 12px' }}>
          <div style={labelRow}>
            Assigned to
            <button onClick={() => setEditingAssignee(v => !v)} style={editBtn}>
              <Edit3 size={12} />
            </button>
          </div>
          {editingAssignee ? (
            <select value={assigneeId}
              onChange={e => { doUpdate({ assignee_id: e.target.value || null }); setEditingAssignee(false); }}
              style={inp} autoFocus>
              <option value="">Unassigned</option>
              {people.map(p => (
                <option key={p.id} value={p.id}>
                  {p.display_name || p.id}
                </option>
              ))}
            </select>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
              onClick={() => setEditingAssignee(true)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (() => setEditingAssignee(true))(e); } }}>
              <Avatar name={assignee} size={24} />
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--t1)' }}>{assignee}</span>
            </div>
          )}
        </div>
        {projName && (
          <div style={{ flex: 1, ...glassBox, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: 'var(--tm)', marginBottom: 4 }}>Project</div>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--t1)' }}>{projName}</div>
          </div>
        )}
      </div>

      {/* Department + Bundle row */}
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1, ...glassBox, padding: '10px 12px' }}>
          <div style={{ fontSize: 11, color: 'var(--tm)', marginBottom: 4 }}>Department</div>
          <select value={f.department_id || ''}
            onChange={e => doUpdate({ department_id: e.target.value || null })}
            style={{
              ...inp, minHeight: 32, padding: '4px 8px',
              background: 'transparent', border: 'none', fontSize: 13, fontWeight: 500,
            }}>
            <option value="">None</option>
            {departments.map(d => (
              <option key={d.id} value={d.id}>{d.name_es}</option>
            ))}
          </select>
        </div>
        {f.project_id && projectBundles.length > 0 ? (
          <div style={{ flex: 1, ...glassBox, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: 'var(--tm)', marginBottom: 4 }}>Bundle</div>
            <select value={f.bundle_id || ''}
              onChange={e => doUpdate({ bundle_id: e.target.value || null })}
              style={{
                ...inp, minHeight: 32, padding: '4px 8px',
                background: 'transparent', border: 'none', fontSize: 13, fontWeight: 500,
              }}>
              <option value="">None</option>
              {projectBundles.map(b => (
                <option key={b.id} value={b.id}>
                  {b.title || b.name || `Bundle ${(b.id || '').slice(-4)}`}
                </option>
              ))}
            </select>
          </div>
        ) : !f.project_id && f.bundle_id ? (
          <div style={{ flex: 1, ...glassBox, padding: '10px 12px', borderColor: 'var(--warn)' }}>
            <div style={{ fontSize: 11, color: 'var(--warn)', marginBottom: 4 }}>Bundle (orphan)</div>
            <div style={{ fontSize: 12, color: 'var(--tm)', fontStyle: 'italic' }}>
              Linked to a bundle but task has no project. Assign a project to re-link.
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
